import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { shared } from "../styles.js";
import { buildPullCommand } from "../lib/pull-command.js";
import { copyIcon } from "../lib/icons.js";

/**
 * copy-pull shows the exact CLI pull command for a package coordinate at a ref
 * (tag or version) and copies it on click (R10), styled as the design's near-black
 * install block: a cyan `$` prompt, the command in mono, and a copy button. The
 * rendered command is exposed in the DOM (the `[data-testid=pull-command]`) so it is
 * testable without a clipboard.
 */
@customElement("copy-pull")
export class CopyPull extends LitElement {
  static override styles = [
    shared,
    css`
      .block {
        display: flex;
        align-items: center;
        gap: 12px;
        background: var(--code-bg);
        border-radius: 9px;
        padding: 11px 14px;
      }
      .prompt {
        font: 500 13px/1 var(--mono);
        color: var(--code-prompt);
      }
      code {
        font: 500 13px/1.3 var(--mono);
        color: var(--code-fg);
        flex: 1;
        overflow: auto;
        white-space: nowrap;
      }
      .copy {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        background: none;
        border: 0;
        cursor: pointer;
        color: var(--code-dim);
        font: 500 11.5px/1 var(--mono);
        padding: 2px;
        flex-shrink: 0;
      }
      .copy:hover {
        color: var(--code-fg);
      }
    `,
  ];

  @property() pkg = "";
  @property() ref = "latest";
  @state() private copied = false;
  private resetTimer?: ReturnType<typeof setTimeout>;

  override disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.resetTimer);
  }

  private async copy() {
    try {
      await navigator.clipboard.writeText(buildPullCommand(this.pkg, this.ref));
      this.copied = true;
      clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(() => (this.copied = false), 1500);
    } catch {
      /* clipboard may be blocked; the command stays visible to copy manually */
    }
  }

  override render() {
    const cmd = buildPullCommand(this.pkg, this.ref);
    return html`
      <div class="block">
        <span class="prompt" aria-hidden="true">$</span>
        <code data-testid="pull-command">${cmd}</code>
        <button class="copy" @click=${this.copy} aria-label="Copy pull command">
          ${this.copied ? "Copied" : copyIcon(14)}
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "copy-pull": CopyPull;
  }
}
